import React from "react";
import styled from "styled-components";
import { colors, radius, shadow } from "./theme";
import { useWindowManager } from "./WindowManager";
import AppIcon from "./AppIcon";

const Wrap = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12px;
  z-index: 100000;
  display: flex;
  justify-content: center;
  pointer-events: none;
`;

const Bar = styled.div<{ $hidden: boolean }>`
  pointer-events: ${({ $hidden }) => ($hidden ? "none" : "auto")};
  display: flex;
  align-items: flex-end;
  gap: 7px;
  padding: 6px 9px;
  background: rgba(253, 253, 251, 0.72);
  backdrop-filter: blur(12px);
  border: 2px solid ${colors.ink};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.soft};
  transition: transform 0.25s ease, opacity 0.25s ease;
  transform: translateY(${({ $hidden }) => ($hidden ? "150%" : "0")});
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
`;

const Item = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
`;

const Tile = styled.div<{ $accent: string }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${radius.md};
  border: 2px solid ${colors.ink};
  background: ${({ $accent }) => $accent};
  color: ${colors.ink};
  box-shadow: 2px 2px 0 rgba(29, 29, 31, 0.18);
  transition: transform 0.16s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: bottom center;

  ${Item}:hover & {
    transform: translateY(-8px) scale(1.14);
  }
`;

const RunningDot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${colors.ink};
  margin-top: 4px;
`;

const Divider = styled.div`
  align-self: stretch;
  width: 2px;
  margin: 2px 3px;
  border-radius: 2px;
  background: rgba(29, 29, 31, 0.2);
`;

const Tooltip = styled.span`
  position: absolute;
  bottom: 58px;
  white-space: nowrap;
  background: ${colors.ink};
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 9px;
  border-radius: ${radius.sm};
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.12s ease, transform 0.12s ease;
  pointer-events: none;

  ${Item}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Dock = () => {
  const { registry, windows, getApp, openApp, focusWindow, isOpen } =
    useWindowManager();
  // App section (left of the divider): pinned apps first, then any running apps
  // that aren't pinned - macOS keeps a running app's icon in the Dock while it's
  // open whether or not it's normally kept there, placed after the pinned ones.
  const pinned = registry.filter((a) => a.pinned);
  const runningNotPinned = registry.filter((a) => !a.pinned && isOpen(a.id));
  const appSection = [...pinned, ...runningNotPinned];
  const minimized = windows.filter((w) => w.minimized);
  const hasFullscreen = windows.some((w) => w.maximized && !w.minimized);

  const handleAppClick = (appId: string) => {
    const existing = windows.find((w) => w.appId === appId);
    if (existing) {
      focusWindow(existing.id);
    } else {
      openApp(appId);
    }
  };

  return (
    <Wrap>
      <Bar $hidden={hasFullscreen} data-dock>
        {appSection.map((app) => (
          <Item key={app.id} onClick={() => handleAppClick(app.id)}>
            <Tooltip>{app.title}</Tooltip>
            <Tile $accent={app.accent}>
              <AppIcon
                icon={app.icon}
                size={20}
                color={app.accent === colors.ink ? "#fff" : colors.ink}
              />
            </Tile>
            {isOpen(app.id) && <RunningDot />}
          </Item>
        ))}

        {minimized.length > 0 && <Divider />}

        {minimized.map((win) => {
          const app = getApp(win.appId);
          if (!app) return null;
          return (
            <Item
              key={win.id}
              data-win-tile={win.id}
              onClick={() => focusWindow(win.id)}
            >
              <Tooltip>{app.title}</Tooltip>
              <Tile $accent={app.accent}>
                <AppIcon
                  icon={app.icon}
                  size={20}
                  color={app.accent === colors.ink ? "#fff" : colors.ink}
                />
              </Tile>
            </Item>
          );
        })}
      </Bar>
    </Wrap>
  );
};

export default Dock;
